/**
 * Licenca da clinica (`system_settings/license`, escrita pela Central do Gaius).
 * Espelha a validacao do servidor (`functions/src/shared/license.ts`): qualquer desvio => null.
 */
export const MODULE_KEYS = ['laudos', 'agenda', 'worklist', 'whatsapp', 'chatbot', 'lista_espera', 'financeiro'] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];
export type ModuleState = 'contratado' | 'so_leitura' | 'ausente';
export type ClinicStatus = 'ativa' | 'so_leitura' | 'cancelada';

export type BotLimits = {
  maxTokensPerDayPerConv: number;
  maxIterations: number;
  maxTokensPerMonth: number | null;
};

export type ParsedLicense = {
  enforcement: 'observar' | 'aplicar';
  status: ClinicStatus;
  modules: Record<ModuleKey, ModuleState>;
  botLimits: BotLimits;
};

const STATES: readonly unknown[] = ['contratado', 'so_leitura', 'ausente'];
const STATUSES: readonly unknown[] = ['ativa', 'so_leitura', 'cancelada'];

function isInt(v: unknown, min: number, max: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
}

export function parseLicense(data: unknown): ParsedLicense | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.schemaVersion !== 1) return null;
  if (typeof d.clinicId !== 'string' || d.clinicId === '') return null;
  if (!isInt(d.version, 1, Number.MAX_SAFE_INTEGER)) return null;
  if (!STATUSES.includes(d.status)) return null;
  const m = d.modules as Record<string, unknown> | null | undefined;
  if (!m || typeof m !== 'object') return null;
  const modules = {} as Record<ModuleKey, ModuleState>;
  for (const k of MODULE_KEYS) {
    if (!STATES.includes(m[k])) return null;
    modules[k] = m[k] as ModuleState;
  }
  const b = d.botLimits as Record<string, unknown> | null | undefined;
  if (!b || typeof b !== 'object') return null;
  const month = b.maxTokensPerMonth;
  if (!isInt(b.maxTokensPerDayPerConv, 1_000, 10_000_000)) return null;
  if (!isInt(b.maxIterations, 1, 10)) return null;
  if (month !== null && !isInt(month, 1_000, Number.MAX_SAFE_INTEGER)) return null;
  const e = d.enforcement;
  if (e != null && e !== 'observar' && e !== 'aplicar') return null;
  return {
    enforcement: e ?? 'observar',
    status: d.status as ClinicStatus,
    modules,
    botLimits: {
      maxTokensPerDayPerConv: b.maxTokensPerDayPerConv,
      maxIterations: b.maxIterations,
      maxTokensPerMonth: month as number | null,
    },
  };
}
